import { Toggle } from "@/components/ui/toggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsContactRevealOffered } from "@/hooks/useIsContactRevealOffered";
import { useIsPeopleSearchOffered } from "@/hooks/useIsPeopleSearchOffered";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ChatRequestMode } from "@shaarp/common";
import {
  MouseEvent,
  PointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  CHAT_REQUEST_MODE_ICONS,
  CHAT_REQUEST_MODE_LABEL_KEYS,
  CHAT_REQUEST_MODES,
  getChatRequestModeDescriptionKey,
} from "./chat-request-modes";

/**
 * The request modes, shown up front rather than in a menu: a choice on screen
 * is made — the search page's companies / people switch was set on every one
 * of its searches — while a choice tucked in the tools menu almost never is,
 * as forcing a tool from it shows.
 *
 * Shortcuts, not a required step: picking one arms the composer — the mode
 * then shows as an inline prefix the user can drop — and sends nothing.
 * Leaving them alone lets the assistant read the message on its own. The pill
 * that is on mirrors that prefix, so dropping either clears both.
 *
 * Only on the welcome screen, where they cascade in above the composer. The
 * docked composer of a conversation does not carry them: the follow-up
 * messages of a thread already have their context.
 *
 * Each pill tells what its mode answers with in a tooltip, before it is
 * picked: from the labels alone, « Un contact » and « Prospection » both read
 * as the place to list people. A finger never opens a tooltip, so on a touch
 * screen the help of the pill last touched shows under the row instead, from
 * the moment the finger lands. Held, a pill only tells its help — the Android
 * convention for a tooltip — so each mode can be read before one is chosen; a
 * tap arms it, and a second one drops it. The line keeps the height of the
 * longest help text, so changing it never moves the composer under the row.
 */
/** How long a finger rests on a pill before the press only reads its help. */
const HELP_PRESS_MS = 450;

/** The touch help line's own text, shown before any pill is touched. */
const TOUCH_HINT = "hint";

export function ChatRequestModePills({
  activeMode,
  onToggle,
  disabled = false,
}: {
  activeMode: ChatRequestMode | null;
  onToggle: (mode: ChatRequestMode) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const isContactRevealOffered = useIsContactRevealOffered();
  // Whether « Prospection » also lists people (#668), as the composer and the
  // request read it. The pills only show on the welcome screen of the
  // standalone chat, never in a conversation about one company.
  const isPeopleSearchOffered = useIsPeopleSearchOffered({
    isCompanyTargeted: false,
  });
  // A team with no key contacts or no reveal channel has no contact card to
  // ask for: the mode would only be refused.
  const offeredModes = CHAT_REQUEST_MODES.filter(
    (mode) => mode !== "contact" || isContactRevealOffered,
  );
  const describe = (mode: ChatRequestMode): string =>
    t(getChatRequestModeDescriptionKey(mode, isPeopleSearchOffered));

  // The pill a finger last landed on, whose help the touch line tells; the
  // armed mode's until then.
  const [touchedMode, setTouchedMode] = useState<ChatRequestMode | null>(null);
  // The press under way, and whether it has lasted long enough to only read.
  const pressRef = useRef<{
    mode: ChatRequestMode;
    timer: number | null;
    isHeld: boolean;
  } | null>(null);
  const clearPressTimer = (): void => {
    const press = pressRef.current;
    if (press?.timer != null) {
      window.clearTimeout(press.timer);
      press.timer = null;
    }
  };
  useEffect(() => clearPressTimer, []);

  const handlePointerDown = (
    mode: ChatRequestMode,
    event: PointerEvent<HTMLButtonElement>,
  ): void => {
    // A mouse keeps the tooltip, and a slow click must still arm.
    if (event.pointerType !== "touch") return;
    clearPressTimer();
    setTouchedMode(mode);
    const press = { mode, timer: null as number | null, isHeld: false };
    press.timer = window.setTimeout(() => {
      press.isHeld = true;
      press.timer = null;
    }, HELP_PRESS_MS);
    pressRef.current = press;
  };

  // A held pill was read, not chosen: the click its release fires does not
  // arm it. Radix leaves a prevented click alone.
  const handleClick = (
    mode: ChatRequestMode,
    event: MouseEvent<HTMLButtonElement>,
  ): void => {
    const press = pressRef.current;
    clearPressTimer();
    pressRef.current = null;
    if (press?.mode === mode && press.isHeld) event.preventDefault();
  };

  const describedMode = touchedMode ?? activeMode;
  const touchHelpShown =
    describedMode && offeredModes.includes(describedMode)
      ? describedMode
      : TOUCH_HINT;
  const touchHelpTexts = [
    { key: TOUCH_HINT, text: t("chat.input.modes.touchHint") },
    ...offeredModes.map((mode) => ({ key: mode, text: describe(mode) })),
  ];

  return (
    <div className="flex flex-col gap-2">
      <div
        role="group"
        aria-label={t("chat.input.modes.title")}
        className="flex flex-wrap gap-2 justify-center"
      >
        {offeredModes.map((mode, index) => {
          const Icon = CHAT_REQUEST_MODE_ICONS[mode];
          const isOn = activeMode === mode;
          const label = t(CHAT_REQUEST_MODE_LABEL_KEYS[mode]);

          return (
            <Tooltip key={mode}>
              <TooltipTrigger asChild>
                <Toggle
                  variant="pill"
                  pressed={isOn}
                  onPressedChange={() => onToggle(mode)}
                  onPointerDown={(event) => handlePointerDown(mode, event)}
                  onPointerUp={clearPressTimer}
                  onPointerCancel={clearPressTimer}
                  onClick={(event) => handleClick(mode, event)}
                  disabled={disabled}
                  // A finger's target on the phone's home screen, at least
                  // 44 px; a mouse keeps the row as compact as it was.
                  className="suggestion-cascade touch:h-11 touch:min-w-11"
                  style={{
                    animationDelay: `calc(var(--cascade-start, 0ms) + ${index} * var(--cascade-step, 60ms))`,
                  }}
                >
                  <Icon className={cn(!isOn && "text-majorelle")} />
                  <span>{label}</span>
                </Toggle>
              </TooltipTrigger>
              <TooltipContent side="bottom">{describe(mode)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      {/* Only where nothing hovers (`touch:`): a mouse keeps the tooltip and
          the row the product keeps. Every text sits in the same cell, the
          ones not told kept invisible, so the line is as tall as the longest
          of them whichever it shows. */}
      <div
        role="status"
        className="hidden px-2 text-xs text-center touch:grid text-muted-foreground"
      >
        {touchHelpTexts.map(({ key, text }) => (
          <p
            key={key}
            aria-hidden={key !== touchHelpShown}
            className={cn(
              "[grid-area:1/1]",
              key !== touchHelpShown && "invisible",
            )}
          >
            {text}
          </p>
        ))}
      </div>
    </div>
  );
}
